import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { EquipmentDto, EquipmentType } from '@operations/app/api'
import { FormProps, SelectOption } from '@operations/app/models'
import { useOperationsStore } from '@operations/AppProvider'
import useFormWithSchema from '@operations/hooks/use-form-with-schema.hook'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import _, { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { EquipmentFormMapper } from './equipment-form.mapper'
import { EquipmentFormProfile, defaultValues } from './equipment-form.profile'
import { schema } from './equipment-form.scheme'

interface Props extends FormProps {
  equipment?: EquipmentDto
  onSubmit: (formValues: EquipmentFormProfile) => Promise<void> | void
}

export const EquipmentForm = ({ id, equipment, onSubmit }: Props) => {
  const { handleSubmit, formState, control, reset, watch } =
    useFormWithSchema<EquipmentFormProfile>(schema(), defaultValues)

  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { equipmentStore } = useOperationsStore()

  const equipmentType = watch('equipmentType') as EquipmentType
  const [equipmentNameExist, setEquipmentNameExist] = useState<boolean>(false)
  const equipmentTypeOptions: SelectOption[] = useMemo(
    () =>
      _(
        Object.values(EquipmentType)
          .filter(x => x !== EquipmentType.Sc)
          .map(x => ({
            label: t(x),
            value: x,
          })),
      )
        .sortBy(i => i.label)
        .value(),
    [t],
  )

  const debouncedCheckEquipmentName = useMemo(
    () =>
      debounce((value: string) => {
        if (
          equipmentStore.items.find(
            i => i.name.trim().toLowerCase() === value.trim().toLowerCase() && i.id !== Number(id),
          )
        ) {
          setEquipmentNameExist(true)
        } else {
          setEquipmentNameExist(false)
        }
      }, 500),
    [id, equipmentStore.items],
  )

  useEffect(() => {
    if (equipment) {
      reset(EquipmentFormMapper.mapDtoToFormValues(equipment))
    }
  }, [reset, equipment])

  const hasMaxStackingHeight = (equipmentType: EquipmentType) => {
    const equipmentTypes: EquipmentType[] = [
      EquipmentType.Rs,
      EquipmentType.Ech,
      EquipmentType.Rtg,
      EquipmentType.Rmg,
    ]

    return equipmentTypes.includes(equipmentType)
  }

  const blockInvalidChar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const invalidChars = ['e', 'E', '+', '-', '.']
    if (invalidChars.includes(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <Box id={id} component='form' noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='subtitle2' marginBottom={theme.customSpacing.m}>
        {t('equipmentDetails', 'Equipment details')}
      </Typography>
      <FormGroup sx={{ gap: theme.customSpacing.l }}>
        <Controller
          control={control}
          name='name'
          render={({ field }) => (
            <TextField
              {...field}
              required
              error={!!formState.errors['name'] || equipmentNameExist}
              label={t('name', 'Name')}
              fullWidth
              helperText={
                equipmentNameExist
                  ? t('nameAlreadyExists', 'Name already exists.')
                  : formState.errors['name']
                    ? t('fieldIsRequired', 'Field is required')
                    : undefined
              }
              onChange={event => {
                field.onChange(event)
                debouncedCheckEquipmentName(event.target.value)
              }}
            />
          )}
        />

        <FormControl fullWidth required>
          <InputLabel>{t('equipmentType', 'Equipment type')}</InputLabel>
          <Controller
            control={control}
            name='equipmentType'
            render={({ field }) => (
              <Select
                {...field}
                error={!!formState.errors['equipmentType']}
                onChange={event => {
                  field.onChange(event.target.value)
                }}
                label={t('equipmentType', 'Equipment type')}
                disabled={!!equipment}
              >
                {equipmentTypeOptions.map(({ value, label }) => (
                  <MenuItem key={label} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {formState.errors['equipmentType'] && (
            <FormHelperText error>{t('fieldIsRequired', 'Field is required')}</FormHelperText>
          )}
        </FormControl>

        <Controller
          control={control}
          name='description'
          render={({ field }) => (
            <TextField {...field} label={t('description', 'Description')} fullWidth />
          )}
        />

        <Grid container spacing={theme.customSpacing.l}>
          <Grid
            size={{
              xs: 12,
              sm: hasMaxStackingHeight(equipmentType) ? 6 : 12,
            }}
          >
            <Controller
              control={control}
              name='maxLiftWeight'
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!formState.errors['maxLiftWeight']}
                  label={t('maxLiftWeight', 'Max lift weight')}
                  value={field.value ?? ''}
                  fullWidth
                  helperText={
                    formState.errors['maxLiftWeight']
                      ? t('fieldCannotHaveANegativeValue', 'Field cannot have a negative value')
                      : undefined
                  }
                  type='number'
                  onKeyDown={blockInvalidChar}
                  slotProps={{
                    input: {
                      endAdornment: <CustomInputAdornment position='end'>kg</CustomInputAdornment>,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {hasMaxStackingHeight(equipmentType) && (
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                control={control}
                name='maxStackingHeight'
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!formState.errors['maxStackingHeight']}
                    label={t('maxStackingHeight', 'Max stacking height')}
                    value={field.value ?? ''}
                    fullWidth
                    type='number'
                    helperText={
                      formState.errors['maxStackingHeight']
                        ? t('fieldCannotHaveANegativeValue', 'Field cannot have a negative value')
                        : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <CustomInputAdornment position='end'>
                          {t('inItems', 'in items')}
                        </CustomInputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        <Stack>
          <Typography variant='subtitle2'>{t('status', 'Status')}</Typography>
          <Controller
            control={control}
            name={'isActive'}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                }
                label={t('available', 'Available')}
              />
            )}
          />
        </Stack>
        <Stack>
          <Typography variant='subtitle2'>{t('ownership', 'Ownership')}</Typography>
          <Controller
            control={control}
            name={'isOwned'}
            render={({ field }) => (
              <RadioGroup
                sx={{ display: 'flex', flexDirection: 'row', gap: theme.customSpacing.m }}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label={t('terminal', 'Terminal')}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label={t('external', 'External')}
                />
              </RadioGroup>
            )}
          />
        </Stack>
      </FormGroup>
    </Box>
  )
}
